import { useState, useRef, useEffect } from 'react';
import { Bell, Plus, BellOff, Building, Sun, Moon, Maximize, Minimize } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
// We're using the LanguageSwitcher component directly, so we don't need this import
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatProfilePictureUrl } from '../../utils/imageUtils';
import UserProfileDropdown from '../user/UserProfileDropdown';
import useClickOutside from '../../hooks/useClickOutside';
import { Ticket } from 'lucide-react';
import { getSubdomain } from '../../utils/tenantUtils';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useFullScreen } from '../../contexts/FullScreenContext';
import { useToast } from '../../contexts/ToastContext';

export default function Header({ setIsSidebarOpen }: { setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>> }) {
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const { isFullScreen, toggleFullScreen } = useFullScreen();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notifications, markAsRead, markAllAsRead, soundEnabled, toggleSound, connectionStatus } = useNotifications();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<string | null>(null);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const unreadCount = notifications.filter(notification => !notification.read).length;
  
  // Get the current tenant subdomain
  useEffect(() => {
    const subdomain = getSubdomain();
    setCurrentTenant(subdomain);
  }, []);

  useClickOutside(dropdownRef, () => setShowProfileDropdown(false));
  useClickOutside(notificationRef, () => setShowNotifications(false));

  // Filter to only show unread notifications
  const unreadNotifications = notifications.filter(notification => !notification.read);

  return (
    <header className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
      <div className="px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left section with menu button */}
          <div className="flex items-center">
            <button
              className={`p-2 md:hidden ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:text-gray-100' 
                  : 'text-gray-600 hover:text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg`}
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              aria-label={t('theme.openMenu')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Center section - can be used for other content */}
          <div className="hidden md:block flex-1 min-w-0 px-4">
            {/* AIQuote component removed */}
          </div>
          
          {/* Right section with actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Switcher */}
            <div className="mr-2">
              <LanguageSwitcher variant="dropdown" showLabel={false} />
            </div>
            
            {/* Theme Switcher */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${theme === 'dark' ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              title={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            
            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullScreen}
              className={`p-2 rounded-lg ${theme === 'dark' ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
              title={isFullScreen ? t('common.exitFullScreen') : t('common.fullScreen')}
            >
              {isFullScreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </button>
            
            {/* Tenant indicator */}
            {currentTenant && (
              <div className={`hidden sm:flex items-center px-3 py-1.5 rounded-md ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <Building className={`h-4 w-4 mr-2 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                  {currentTenant}
                </span>
              </div>
            )}
            
            {/* Create Ticket Button */}
            <button
              onClick={() => navigate('/create-ticket')}
              className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('common.createTicket')}
            </button>
            
            <div className="relative" ref={notificationRef}>
              <button 
                className={`relative p-2 ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-gray-100'
                    : 'text-gray-600 hover:text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg`}
                aria-label={t('common.notifications')}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 text-xs font-medium text-white bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-800 transition-all duration-200 ease-in-out transform scale-100">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div 
                  className={`absolute right-0 mt-2 w-96 rounded-lg shadow-lg py-1 z-50 transform opacity-100 scale-100 transition-all duration-200 ease-out
                    ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                    ring-1 ring-black ring-opacity-5`}
                  style={{ maxHeight: 'calc(100vh - 200px)' }}
                >
                  <div className="sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 flex justify-between items-center backdrop-blur-sm bg-opacity-75 dark:bg-opacity-75">
                    <div className="flex items-center space-x-2">
                      <h3 className={`text-base font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                        {t('common.notifications')}
                      </h3>
                      <div className={`text-xs px-2 py-0.5 rounded-full ${
                        connectionStatus === 'connected'
                          ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                          : connectionStatus === 'error'
                          ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400'
                      }`}>
                        {connectionStatus === 'connected' ? t('common.connected') : connectionStatus === 'error' ? t('common.connectionError') : t('common.disconnected')}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          toggleSound().catch(error => {
                            console.warn('Failed to toggle sound:', error);
                          });
                        }}
                        className={`p-1.5 rounded-full transition-colors ${
                          soundEnabled
                            ? theme === 'dark'
                              ? 'text-blue-400 hover:text-blue-300'
                              : 'text-blue-600 hover:text-blue-500'
                            : theme === 'dark'
                              ? 'text-gray-500 hover:text-gray-400'
                              : 'text-gray-400 hover:text-gray-500'
                        }`}
                        title={soundEnabled ? t('notifications.disableSound') : t('notifications.enableSound')}
                      >
                        {soundEnabled ? (
                          <Bell className="h-5 w-5" />
                        ) : (
                          <BellOff className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await markAllAsRead();
                          } catch (error) {
                            alert(t('notifications.markAllReadError'));
                          }
                        }}
                        className={`text-sm font-medium ${
                          theme === 'dark'
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-blue-600 hover:text-blue-500'
                        } transition-colors`}
                      >
                        {t('common.markAllAsRead')}
                      </button>
                    </div>
                  </div>
                  
                  <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                    {unreadNotifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-600 mb-3" />
                        <p className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {t('common.noNotifications')}
                        </p>
                      </div>
                    ) : (
                      unreadNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`group px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors duration-150 ${
                            !notification.read ? 'bg-blue-50/80 dark:bg-blue-900/20' : ''
                          }`}
                          onClick={async () => {
                            try {
                              // Log the notification for debugging
                              if(notification.type === 'bulk_update') {
                                showToast(notification.ticket?.description || '', 'success');
                                await markAsRead(notification.id);
                                return;
                              }
                              
                              // Store the ticket ID before marking as read to ensure we have it
                              const ticketId = notification.ticket?.id;
                              
                              if (!ticketId) {
                                console.error('No ticket ID found in notification');
                                alert(t('notifications.noAssociatedTicket'));
                                return;
                              }
                              
                              // Close the notification dropdown immediately for better UX
                              setShowNotifications(false);
                              
                              // Mark notification as read and ensure it completes
                              await markAsRead(notification.id);
                              
                              navigate(`/tickets/${ticketId}`);
                            } catch (error) {
                              console.error('Error handling notification click:', error);
                              // If marking as read fails, still try to navigate to the ticket
                              const ticketId = notification.ticket?.id;
                              if (ticketId) {
                                navigate(`/tickets/${ticketId}`);
                              } else {
                                alert(t('notifications.navigationError'));
                              }
                            }
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                              <Ticket className={`h-5 w-5 ${
                                theme === 'dark' 
                                  ? 'text-blue-400 group-hover:text-blue-300' 
                                  : 'text-blue-600 group-hover:text-blue-500'
                              } transition-colors`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${
                                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                                }`}>
                                  {notification.message || (notification.ticket ? t('notifications.ticketUpdated', { id: notification.ticket.id }) : t('notifications.received'))}
                                </p>
                                <span className="ml-2 text-xs text-gray-400 whitespace-nowrap">
                                  {new Date(notification.createdAt).toLocaleString(i18n.language, {timeZone: 'Asia/Yangon'})}
                                </span>
                              </div>
                              {/* Only show ticket title if it's different from the notification message */}
                              {notification.ticket && notification.ticket.title && !notification.message.includes(notification.ticket.title) && (
                                <p className={`mt-1 text-sm line-clamp-2 ${
                                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  {notification.ticket.title}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className={`flex items-center space-x-3 p-1.5 rounded-full ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                } transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500`}
                aria-label={t('common.profile')}
              >
                <div className="h-8 w-8 rounded-full overflow-hidden">
                  {user?.pfp_url ? (
                    <img 
                      src={formatProfilePictureUrl(user.pfp_url)} 
                      alt="Profile" 
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const imgElement = e.target as HTMLImageElement;
                        imgElement.style.display = 'none';
                        const nextElement = imgElement.nextElementSibling as HTMLElement;
                        if (nextElement) {
                          nextElement.style.display = 'flex';
                        }
                        console.error('Failed to load profile image:', user.pfp_url);
                      }}
                    />
                  ) : null}
                  <div 
                    className={`${user?.pfp_url ? 'hidden' : 'flex'} h-full w-full bg-blue-500 items-center justify-center`}
                  >
                    <span className="text-white font-medium">
                      {user?.full_name ? user.full_name.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                </div>
              </button>
              {showProfileDropdown && (
                <UserProfileDropdown onClose={() => setShowProfileDropdown(false)} />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
